import React from 'react';
import { Grid } from 'components/base';
import { getTotalPrice } from 'data/lib/product';
import { formatPrice } from 'utils/format';

import useEditorItemContext from '../../context/editorItemContext';
import {
  getOriginalPage,
  getOtherOrientationPage,
  isRemovePageEnabled,
  isDuplicatePageEnabled,
  getOtherPageOfGroup,
  getPossiblePagesVariants,
  getPagesFilterVariants,
  isChangePageEnabled,
  isSelectInitialPageEnabled,
  getTransformedPage,
  getBleedZone,
} from 'editor-utils/template';
import pagesValidator from '../../lib/pagesValidator';
import { formatData } from 'editor-utils/dataParser';
import { getPagesOfAlternativeProducts } from '../../lib/product';

import PageWrapper from '../../components/page/wrapper';
import Page from '../../components/page/preview';
import PhotoQualityAlert, { usePhotosQuality } from '../../components/photoQualityAlert';
import generateClassicData from '../../generator/generateClassicData';
import { generateUpdatedPage } from '../../generator/utils';
import useEditorContext from '../../context/editorContext';
import usePhotoWellContext from '../../context/photoWellContext';
import usePhotoCropperContext from '../../context/photoCropperContext';
import SwitchPanel from '../components/switchPanel';
import DesignSelect from '../components/designSelect';
import SelectPanel from '../components/selectPanel';
import EditorLayout from '../components/layout';
import OptionedContinueButton from '../components/optionedContinueButton';
import useEditorRouter from '../../lib/editorRouter';
import MultiProductDesignSelect from '../components/multiProductDesignSelect';
import TooltipPanel from '../components/tooltipPanel';

const DefaultPreview = props => {
  const editorContext = useEditorContext();

  const { item, product, template, config, alternativeProducts } = useEditorItemContext();

  const editorRouter = useEditorRouter(product.id);

  const photoWell = usePhotoWellContext();
  const photoCropper = usePhotoCropperContext();

  const gridColProps = {
    xs: config.numberOfPages.xs ? 12 / config.numberOfPages.xs : undefined,
    sm: config.numberOfPages.sm ? 12 / config.numberOfPages.sm : undefined,
    md: config.numberOfPages.md ? 12 / config.numberOfPages.md : undefined,
    lg: config.numberOfPages.lg ? 12 / config.numberOfPages.lg : undefined,
    xl: config.numberOfPages.xl ? 12 / config.numberOfPages.xl : undefined,
  };

  const handleOnSuccessClick = async (checkErrors = true) => {
    const validator = pagesValidator(template, item.pages, config);
    const data = formatData(template, item, { validator });

    if (validator.isValid() || !checkErrors) {
      props.onSuccess({ data });
      return { data };
    }

    const errors = validator.getErrors();
    props.onSuccess({ errors });
    return { errors };
  };

  const [photosQuality, handleOnPhotosQuality, acceptableQuality] = usePhotosQuality(
    template.template.minDPI,
  );

  const handleOnChangePages = pageId => {
    const newPage = generateUpdatedPage(template, item.pages[0], pageId);
    editorContext.changePage(item.id, newPage, { pageIndex: 0 });
  };

  let onRemovePage;
  if (isRemovePageEnabled(template, item.pages)) {
    onRemovePage = (e, index) => {
      e.preventDefault();

      editorContext.removePage(item.id, index);
    };
  }

  let onDuplicatePage;
  if (isDuplicatePageEnabled(template, item.pages)) {
    onDuplicatePage = (e, index) => {
      e.preventDefault();

      editorContext.duplicatePage(item.id, index);
    };
  }

  const onChangePageOrientation = (e, index, pageId) => {
    e.preventDefault();

    const newPage = generateUpdatedPage(template, item.pages[index], pageId);
    editorContext.changePage(item.id, newPage, { pageIndex: index });
  };

  const handleOnChangePagesGroup = (type, checked) => {
    //
    // TODO
    // Type and checked value defines how to transform pages
    //
    editorContext.setPages(
      item.id,
      item.pages.map(p => ({
        ...p,
        id: getOtherPageOfGroup(template, p).id,
        // TODO reset photos
      })),
    );
  };

  const handleOnChangePagesFilter = (type, value) => {
    editorContext.setPages(
      item.id,
      item.pages.map(p => ({
        ...p,
        filter: value ? type : undefined,
      })),
    );
  };

  const handleOnChangeInitialPage = async initialPageId => {
    const { data, error } = await generateClassicData(
      editorContext.accessToken,
      template,
      item.photos,
      initialPageId,
    );
    if (!error) {
      editorContext.setPages(item.id, data.pages);
    }
  };

  const handleOnPagePhotoUpload = (pageIndex, placeholder) => {
    photoWell.show({
      uploadEnabled: true,
      onSelectPhotos: selectedPhotos => {
        editorContext.addPhotoToPlaceholder(item.id, pageIndex, placeholder.id, selectedPhotos[0]);
        photoWell.hide();
      },
    });
  };

  const handleOnPagePhotoEdit = (pageIndex, placeholder, photo) => {
    photoCropper.show({
      placeholder,
      photo,
      onSuccess: data => {
        editorContext.setPhotoData(item.id, pageIndex, placeholder.id, data);
      },
    });
  };

  const handleOnPageTextChange = (pageIndex, placeholder, value, override) => {
    editorContext.setTextData(item.id, pageIndex, placeholder.id, value, override);
  };

  const handleOnChangeProduct = async (newSku, data = {}) => {
    await editorRouter.changeProduct(newSku, template.version, '/preview');
    editorContext.changeProduct(item.id, product.id, newSku, data);
  };

  const handleOnChangeProductAndPage = async (newSku, newPageId) => {
    const isSameSku = newSku === product.id;

    if (isSameSku) {
      handleOnChangePages(newPageId);
    } else {
      const { template: newTemplate } = alternativeProducts.find(p => p.id === newSku);
      await handleOnChangeProduct(newSku, {
        pages: item.pages.map(page => generateUpdatedPage(newTemplate, page, newPageId)),
      });
    }
  };

  const necessaryToReplacePhotos = config.withQualityCheck && !acceptableQuality;

  const sideToolbars = [];

  const pageVariantToolbar = getPossiblePagesVariants(
    template,
    item.pages,
    ({ type, checked, title, description }) => (
      <SwitchPanel
        key={type}
        title={title}
        checked={checked}
        description={description}
        type={type}
        onChange={value => handleOnChangePagesGroup(type, value)}
        className="mb-3"
      />
    ),
  );
  if (pageVariantToolbar.length > 0) {
    sideToolbars.push(...pageVariantToolbar);
  }

  const pageFilterVariantToolbar = getPagesFilterVariants(
    template,
    item.pages,
    ({ type, checked, title, description }) => (
      <SwitchPanel
        key={type}
        title={title}
        checked={checked}
        description={description}
        type={type}
        onChange={value => handleOnChangePagesFilter(type, value)}
        className="mb-3"
      />
    ),
  );
  if (pageFilterVariantToolbar.length > 0) {
    sideToolbars.push(...pageFilterVariantToolbar);
  }

  if (alternativeProducts.length > 1) {
    if (config.withMultiProductDesignSelect) {
      const items = getPagesOfAlternativeProducts(alternativeProducts);

      sideToolbars.push(
        <MultiProductDesignSelect
          key="multiProductDesignSelect"
          product={product}
          template={template}
          item={item}
          items={items}
          onChange={handleOnChangeProductAndPage}
        />,
      );
    } else {
      sideToolbars.push(
        <DesignSelect
          key="sku"
          title="PICK A PRODUCT"
          active={product.id}
          onChange={handleOnChangeProduct}
          items={alternativeProducts.map(alternative => ({
            key: alternative.id,
            title: alternative.templateMetaData.name,
            subTitle:
              product.price !== alternative.price
                ? `${product.price > alternative.price ? '-' : '+'}${formatPrice(
                  Math.abs(product.price - alternative.price),
                  alternative.currency,
                )}`
                : null,
          }))}
          className="mb-3"
        />,
      );
    }
  } else if (isChangePageEnabled(template, item.pages)) {
    sideToolbars.push(
      <DesignSelect
        key="change_page_toolbar"
        title="PICK A DESIGN"
        active={item.pages[0].id}
        onChange={handleOnChangePages}
        items={template.pages.map(page => ({
          key: page.id,
          category: page.caption,
          preview: (
            <Page
              page={getTransformedPage(template, item.pages[0], page.id)}
              template={template}
              photos={item.photos}
              preview
            />
          ),
        }))}
        className="mb-3"
      />,
    );
  }

  const initialPageToolbar = isSelectInitialPageEnabled(template, initialPages => (
    <SelectPanel
      key="initial_page_toolbar"
      active={item.pages[0].id}
      title="OUR CALENDAR IS RATHER SPECIAL"
      description="You can choose when you want it to start. It doesn’t have to be January!"
      items={initialPages.map(p => ({ id: p.id, label: p.label }))}
      onChange={handleOnChangeInitialPage}
      className="mb-3"
    />
  ));
  if (initialPageToolbar) {
    sideToolbars.push(initialPageToolbar);
  }

  sideToolbars.push(<TooltipPanel key="tooltip" />);

  return (
    <EditorLayout
      actionBar={props.actionBar}
      price={getTotalPrice(product, item.pages.length)}
      actionButton={
        <OptionedContinueButton onClick={handleOnSuccessClick}>Add to Cart</OptionedContinueButton>
      }
      toolbars={sideToolbars}
    >
      <>
        <PhotoQualityAlert
          visible={config.withQualityCheck}
          photosQuality={photosQuality}
          minDPI={template.template.minDPI}
          bestDPI={template.template.bestDPI}
          className="mb-3"
        />

        <Grid.Row className={config.rowClassName}>
          {item.pages.map((page, index) => {
            const originalPage = getOriginalPage(template, page);
            const otherOrientationPage = getOtherOrientationPage(template, page);

            return (
              <Grid.Col key={page.key} {...gridColProps} className="col-page-preview mb-1">
                <PageWrapper
                  page={originalPage}
                  fullWidth={config.fullColWidth}
                  onDuplicate={onDuplicatePage ? e => onDuplicatePage(e, index) : undefined}
                  onRemove={onRemovePage ? e => onRemovePage(e, index) : undefined}
                  onChangeOrientation={
                    otherOrientationPage
                      ? e => onChangePageOrientation(e, index, otherOrientationPage.id)
                      : undefined
                  }
                >
                  <Page
                    page={getOriginalPage(template, page)}
                    photos={item.photos}
                    template={template}
                    withQualityCheck={config.withQualityCheck}
                    fullWidth={config.fullColWidth}
                    onPhotoUpload={(...data) => handleOnPagePhotoUpload(index, ...data)}
                    onPhotoEdit={(...data) => handleOnPagePhotoEdit(index, ...data)}
                    onTextChange={(...data) => handleOnPageTextChange(index, ...data)}
                    onPhotosQuality={goodQuality =>
                      handleOnPhotosQuality(`page-${index}`, goodQuality)
                    }
                    interactive
                    bleed={getBleedZone(template, page)}
                  />
                </PageWrapper>
              </Grid.Col>
            );
          })}
        </Grid.Row>
      </>
    </EditorLayout>
  );
};

export default DefaultPreview;
